import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { statsApi, type AttendanceRow } from '../features/stats/api.ts';
import { termsApi } from '../features/terms/api.ts';
import { dv } from '../i18n/dv.ts';
import { type Body, bodyOfRole } from '../lib/body.ts';
import { formatDvDate, meetingNumber } from '../lib/dvDate.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';
import { useAuthStore } from '../store/auth.ts';

// One expandable member: the summary row (rate bar) stays as before; expanding
// reveals a strip of per-meeting dots — success = attended, error = absent —
// reusing the minvaru compliance heat-cell visual language.
function MemberRow({ r }: { r: AttendanceRow }): JSX.Element {
  const [open, setOpen] = useState(false);
  const canExpand = r.meetings.length > 0;
  return (
    <li className="space-y-2 border-b border-black/[0.05] pb-3.5 last:border-b-0 last:pb-0">
      <button
        type="button"
        onClick={() => canExpand && setOpen((o) => !o)}
        aria-expanded={open}
        disabled={!canExpand}
        className="flex w-full items-center gap-2 text-start disabled:cursor-default"
      >
        <Icon
          name="expand_more"
          size={20}
          className={`shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''} ${canExpand ? '' : 'invisible'}`}
        />
        <span className="min-w-0 flex-1 truncate text-sm">
          <span className="font-medium">{r.name}</span>
          {r.role && <span className="ms-2 text-xs text-muted-foreground">{r.role}</span>}
        </span>
        <span className="shrink-0 tabular text-sm text-muted-foreground" dir="ltr">
          {r.attended}/{r.total} · {r.rate}%
        </span>
      </button>

      <div className="h-3.5 overflow-hidden rounded-full bg-[hsl(var(--surface-container-highest))]" role="img" aria-label={`${r.name}: ${r.attended}/${r.total}`}>
        <div className="h-full overflow-hidden rounded-full bg-[hsl(var(--primary))]" style={{ width: `${r.rate}%` }} />
      </div>

      {open && (
        <div className="flex flex-wrap gap-1 pt-1" dir="ltr">
          {r.meetings.map((m, i) => (
            <span
              key={`${m.year}-${m.sequence}-${i}`}
              className={`size-4 rounded-sm ${m.attended ? 'bg-[hsl(var(--success))]' : 'bg-[hsl(var(--destructive))]'}`}
              title={`${meetingNumber(m.year, m.sequence)} · ${formatDvDate(m.scheduledAt)} — ${m.attended ? dv.stats.attendance.attended : dv.stats.attendance.absent}`}
            />
          ))}
        </div>
      )}
    </li>
  );
}

// «މެންބަރުންގެ ހާޒިރީ» — per-councilor attendance across the body's meetings
// with an APPROVED yaumiyya, scoped to a term. Members only (secretariat +
// observers are excluded server-side). Shown as a ranked list with a rate bar.
export function StatsAttendance(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const [body, setBody] = useState<Body>(() => (role ? bodyOfRole(role) : null) ?? 'COUNCIL');
  // '' = all terms. Defaults to the body's active term once the list loads.
  const [termId, setTermId] = useState<string>('');

  const termsQ = useQuery({ queryKey: ['terms', body], queryFn: () => termsApi.list(body) });
  const terms = useMemo(() => termsQ.data ?? [], [termsQ.data]);

  // When the body changes (or terms first load), snap the selector to the active
  // term of that body so the default view is the current dawru.
  useEffect(() => {
    if (!terms.length) return;
    const active = terms.find((t) => t.isActive);
    setTermId(active ? active.id : '');
  }, [terms]);

  const q = useQuery({
    queryKey: ['stats', 'attendance', body, termId],
    queryFn: () => statsApi.attendance(body, termId || undefined),
  });

  const rows = q.data?.rows ?? [];

  return (
    <div className="space-y-6">
      {/* Toolbar — body + term filters. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <BodyToggle body={body} onChange={setBody} />
          {terms.length > 0 && (
            <select
              aria-label={dv.meetings.term}
              value={termId}
              onChange={(e) => setTermId(e.target.value)}
              className="select w-auto rounded-full py-1.5"
            >
              {terms.map((t) => (
                <option key={t.id} value={t.id}>{t.number} ވަނަ ދައުރު</option>
              ))}
              <option value="">{dv.meetings.allTerms}</option>
            </select>
          )}
        </div>
      </div>

      {q.isLoading ? (
        <p className="rounded-3xl bg-[hsl(var(--chrome))] p-6 text-sm text-muted-foreground" role="status" aria-live="polite">
          {dv.common.loading}
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-3xl bg-[hsl(var(--chrome))] p-8 text-center text-sm text-muted-foreground">{dv.stats.attendance.noData}</p>
      ) : (
        <section className="space-y-5 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="mono-meta text-muted-foreground" dir="rtl">
              {dv.stats.attendance.meetingsCounted(q.data!.meetingsCount)}
            </p>
            {/* Legend for the per-meeting dots (shown when a member is expanded). */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><span className="size-3 rounded-sm bg-[hsl(var(--success))]" aria-hidden />{dv.stats.attendance.attended}</span>
              <span className="inline-flex items-center gap-1.5"><span className="size-3 rounded-sm bg-[hsl(var(--destructive))]" aria-hidden />{dv.stats.attendance.absent}</span>
            </div>
          </div>

          <ul className="space-y-3.5">
            {rows.map((r) => (
              <MemberRow key={r.name} r={r} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
